import React, { useEffect, useState } from 'react';
import { Modal, Button, Tag, Card, Typography, Row, Col, Space, Tooltip, Progress, Divider, message, Spin, Popover } from 'antd';
import {
  EyeOutlined,
  YoutubeOutlined,
  LinkOutlined,
  LoadingOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { PlayoutStatus, YouTubeLiveStream, YouTubeLiveStreamBroadcast } from './types';
import { fetchPlayoutStatus, listBroadcasts, refreshBroadcasts, refreshStreamHealth, transitionBroadcastLive } from './api';

const { Text } = Typography;

// Cap on how many file rows we render at once. A schedule-aligned stream can have
// hundreds/thousands of files in its queue; rendering every one as a rich card on
// each 3s status poll is what makes the preview modal lock up over time. We render
// a window around the currently-playing file instead — enough to scroll through
// the active region without materializing the whole playlist.
const MAX_RENDERED_FILES = 150;

const fileBackground = (state: string) =>
  state === 'playing' ? '#fff7e6'
  : state === 'ready' ? '#f6ffed'
  : state === 'downloading' ? '#e6fffb'
  : state === 'completed' ? '#fafafa'
  : state === 'failed' ? '#fff1f0'
  : '#f0f5ff';

const fileBorder = (state: string) =>
  state === 'playing' ? '#ffd591'
  : state === 'ready' ? '#b7eb8f'
  : state === 'downloading' ? '#87e8de'
  : state === 'completed' ? '#d9d9d9'
  : state === 'failed' ? '#ffccc7'
  : '#d6e4ff';

const fileTagColor = (state: string) =>
  state === 'ready' ? 'success'
  : state === 'downloading' ? 'processing'
  : state === 'normalizing' ? 'purple'
  : state === 'playing' ? 'warning'
  : state === 'completed' ? 'default'
  : state === 'failed' ? 'error'
  : 'default';

const FileRow: React.FC<{ file: any }> = ({ file }) => {
  const fileName =
    file.fileName || file.file_name || (file.localPath && file.localPath.split('/').pop());
  const showDownloadProgress =
    file.state === 'downloading' && file.downloadProgress !== undefined && file.downloadProgress !== null;
  return (
    <div
      data-file-index={file.index}
      style={{
        padding: '6px 8px',
        marginBottom: '4px',
        background: fileBackground(file.state),
        borderRadius: '4px',
        fontSize: '11px',
        border: `1px solid ${fileBorder(file.state)}`
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: showDownloadProgress ? '4px' : '0'
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.index + 1}. {file.videoTitle}
          </div>
          {fileName && (
            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px', wordBreak: 'break-word' }}>
              {fileName}
            </div>
          )}
        </div>
        <Tag color={fileTagColor(file.state)} style={{ margin: 0, fontSize: '10px', marginLeft: '8px' }}>
          {file.state === 'normalizing' ? 'transcoding' : file.state}
          {file.state === 'downloading' &&
            file.downloadProgress !== undefined &&
            file.downloadProgress !== null && (
              <span style={{ marginLeft: '4px' }}>{Math.round(file.downloadProgress)}%</span>
            )}
          {file.state === 'normalizing' &&
            file.normalizeProgress !== undefined &&
            file.normalizeProgress !== null && (
              <span style={{ marginLeft: '4px' }}>{Math.round(file.normalizeProgress)}%</span>
            )}
        </Tag>
      </div>
      {showDownloadProgress && (
        <div style={{ marginTop: '4px' }}>
          <Progress
            percent={Math.max(0, Math.min(100, Math.round(file.downloadProgress)))}
            size="small"
            status="active"
            strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
            style={{ margin: 0 }}
          />
          <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '2px', textAlign: 'right' }}>
            {Math.round(file.downloadProgress)}%
          </Text>
        </div>
      )}
      {file.error && (
        <Text type="danger" style={{ fontSize: '10px', display: 'block', marginTop: '4px' }}>
          Error: {file.error}
        </Text>
      )}
    </div>
  );
};

// Renders the queue file list, windowed to MAX_RENDERED_FILES around the playing
// file so a long playlist never materializes thousands of DOM nodes per poll.
const FileList: React.FC<{
  files: any[];
  containerRef: React.RefObject<HTMLDivElement>;
  playingIndex?: number;
}> = ({ files, containerRef, playingIndex }) => {
  const total = files.length;
  let windowStart = 0;
  let rendered = files;

  if (total > MAX_RENDERED_FILES) {
    let anchor = playingIndex != null ? files.findIndex((f) => f.index === playingIndex) : -1;
    if (anchor < 0) {
      anchor = files.findIndex(
        (f) => f.state === 'downloading' || f.state === 'normalizing' || f.state === 'ready'
      );
    }
    if (anchor < 0) anchor = 0;
    windowStart = Math.max(0, Math.min(anchor - Math.floor(MAX_RENDERED_FILES / 2), total - MAX_RENDERED_FILES));
    rendered = files.slice(windowStart, windowStart + MAX_RENDERED_FILES);
  }

  return (
    <>
      {total > MAX_RENDERED_FILES && (
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '6px' }}>
          Showing {windowStart + 1}–{windowStart + rendered.length} of {total} files (windowed for performance)
        </Text>
      )}
      <div ref={containerRef} style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {rendered.map((file: any) => (
          <FileRow key={file.index} file={file} />
        ))}
      </div>
    </>
  );
};

interface PreviewModalProps {
  open: boolean;
  previewStream: YouTubeLiveStream | null;
  // True when the parent just initiated Start for this stream — lets the modal
  // show "Preparing…" immediately, before the playout-server status/queue exist.
  isStarting?: boolean;
  closePreview: () => void;
  isLocalTestingStream: (stream: YouTubeLiveStream) => boolean;
  getHlsUrl: (stream: YouTubeLiveStream) => string;
  getEpgUrl: (stream: YouTubeLiveStream) => string;
  getPreviewPageUrl: (stream: YouTubeLiveStream) => string;
  getYouTubeEmbedUrl: (url: string) => string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  previewStream,
  isStarting = false,
  closePreview,
    isLocalTestingStream,
  getHlsUrl,
  getEpgUrl,
  getPreviewPageUrl,
  getYouTubeEmbedUrl
}) => {
  const isOAuthStream = previewStream?.stream_mode === 'oauth';
  const canRefreshStreamHealth = !!previewStream?.youtube_oauth_stream_id;
  // Playout-server status is polled here (not by the parent page) so a refresh
  // only re-renders this modal, not the whole stream table.
  const [previewStreamStatus, setPreviewStreamStatus] = useState<PlayoutStatus | null>(null);
  const previousStreamRunningRef = React.useRef<boolean>(false);
  const [broadcasts, setBroadcasts] = useState<YouTubeLiveStreamBroadcast[]>([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [streamHealth, setStreamHealth] = useState<YouTubeLiveStream | null>(null);
  const [streamHealthLoading, setStreamHealthLoading] = useState(false);
  const [transitioningLive, setTransitioningLive] = useState(false);
  const [transitionedLive, setTransitionedLive] = useState(false);
  const transitionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const transitionStateRef = React.useRef({ transitioning: false, transitioned: false });
  const fileListRef = React.useRef<HTMLDivElement | null>(null);
  const TRANSITION_DELAY_MS = 15_000;

  const loadBroadcasts = async () => {
    if (!previewStream?.id) return;
    setBroadcastsLoading(true);
    try {
      const data = await listBroadcasts(previewStream.id);
      setBroadcasts(data);
    } catch (error) {
      console.error('Error loading broadcasts:', error);
      message.error('Failed to load broadcasts');
    } finally {
      setBroadcastsLoading(false);
    }
  };

  const handleRefreshBroadcasts = async (showMessage = true) => {
    if (!previewStream?.id) return;
    setBroadcastsLoading(true);
    try {
      const data = await refreshBroadcasts(previewStream.id);
      setBroadcasts(data);
      if (showMessage) {
        message.success('Broadcast statuses refreshed');
      }
    } catch (error) {
      console.error('Error refreshing broadcasts:', error);
      if (showMessage) {
        message.error('Failed to refresh broadcast statuses');
      }
    } finally {
      setBroadcastsLoading(false);
    }
  };

  const handleRefreshStreamHealth = async () => {
    if (!previewStream?.id) return;
    setStreamHealthLoading(true);
    try {
      const updatedStream = await refreshStreamHealth(previewStream.id);
      setStreamHealth(updatedStream);
      message.success('YouTube stream health refreshed');
      if (updatedStream?.stream_status === 'active') {
        scheduleTransitionLive();
      }
    } catch (error) {
      console.error('Error refreshing stream health:', error);
      message.error('Failed to refresh YouTube stream health');
    } finally {
      setStreamHealthLoading(false);
    }
  };

  const scheduleTransitionLive = () => {
    if (!previewStream?.id) return;
    const { transitioning, transitioned } = transitionStateRef.current;
    if (transitioning || transitioned) return;
    if (transitionTimeoutRef.current) return;

    transitionTimeoutRef.current = setTimeout(async () => {
      transitionTimeoutRef.current = null;
      await attemptTransitionLive();
    }, TRANSITION_DELAY_MS);
  };

  const attemptTransitionLive = async () => {
    if (!previewStream?.id) return;
    const { transitioning, transitioned } = transitionStateRef.current;
    if (transitioning || transitioned) return;
    setTransitioningLive(true);
    transitionStateRef.current.transitioning = true;
    try {
      await transitionBroadcastLive(previewStream.id);
      await handleRefreshBroadcasts(false);
      setTransitionedLive(true);
      transitionStateRef.current.transitioned = true;
      message.success('Broadcast marked live');
    } catch (error: any) {
      const isInactive = error?.response?.status === 409;
      if (!isInactive) {
        console.error('Error transitioning broadcast live:', error);
        message.warning('Failed to mark broadcast live');
      }
    } finally {
      setTransitioningLive(false);
      transitionStateRef.current.transitioning = false;
    }
  };

  useEffect(() => {
    if (!open || !previewStream?.id || !isOAuthStream) {
      return;
    }
    setStreamHealth(previewStream);
    loadBroadcasts();
    setTransitioningLive(false);
    setTransitionedLive(false);
    transitionStateRef.current = { transitioning: false, transitioned: false };
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, [open, previewStream?.id, isOAuthStream]);

  useEffect(() => {
    if (!open || !previewStream?.id || !isOAuthStream) {
      return;
    }
    let cancelled = false;

    const pollStreamHealth = async () => {
      try {
        const updatedStream = await refreshStreamHealth(previewStream.id);
        if (cancelled) return;
        setStreamHealth(updatedStream);
        if (updatedStream?.stream_status === 'active') {
          scheduleTransitionLive();
        }
      } catch (error) {
        console.error('Error polling YouTube stream health:', error);
      }
    };

    pollStreamHealth();
    const interval = setInterval(pollStreamHealth, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, [open, previewStream?.id, isOAuthStream]);

  // Poll playout-server status while the modal is open. A plain YouTube-embed
  // preview (video_url only, no playout server / RTMP output) has nothing to poll,
  // so we skip it — and we skip while the tab is hidden so a backgrounded modal
  // stops hammering the server.
  const hasPlayoutStatus = !!(
    previewStream?.playout_server_id ||
    previewStream?.rtmp_url ||
    previewStream?.output_target === 'amagi_hls'
  );
  useEffect(() => {
    if (!open || !previewStream?.id || !hasPlayoutStatus) {
      setPreviewStreamStatus(null);
      previousStreamRunningRef.current = false;
      return;
    }
    const streamId = previewStream.id;
    let cancelled = false;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const newStatus = await fetchPlayoutStatus(streamId);
        if (cancelled) return;
        const isCurrentlyRunning = newStatus?.ffmpeg?.isRunning || false;
        const wasRunning = previousStreamRunningRef.current;
        if (!wasRunning && isCurrentlyRunning) {
          message.success('Stream has started');
        } else if (wasRunning && !isCurrentlyRunning) {
          message.warning('Stream has stopped');
        }
        previousStreamRunningRef.current = isCurrentlyRunning;
        setPreviewStreamStatus(newStatus);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching preview stream status:', error);
        }
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, previewStream?.id, hasPlayoutStatus]);

  // Keep the currently-playing file in view as playout advances to the next one.
  const playingFileIndex: number | undefined =
    previewStreamStatus?.downloadQueue?.currentlyPlaying?.index ??
    previewStreamStatus?.downloadQueue?.allFiles?.find((f: any) => f.state === 'playing')?.index;
  useEffect(() => {
    const container = fileListRef.current;
    if (!container || playingFileIndex == null) return;
    const row = container.querySelector(`[data-file-index="${playingFileIndex}"]`) as HTMLElement | null;
    if (!row) return;
    // Scroll only this container (not the whole modal) so the row sits mid-view.
    const delta = (row.getBoundingClientRect().top - container.getBoundingClientRect().top)
      - (container.clientHeight / 2 - row.clientHeight / 2);
    container.scrollTop += delta;
  }, [playingFileIndex]);

  return (
    <Modal
      title={
        <div>
          {previewStream && (previewStream.rtmp_url || previewStream.output_target === 'amagi_hls') && !previewStream.video_url ? (
            <>
              <EyeOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
              {previewStream?.name}
              <Tag color="blue" style={{ marginLeft: '8px' }}>
                {previewStream.output_target === 'amagi_hls'
                  ? 'Amagi (HLS pull)'
                  : isLocalTestingStream(previewStream)
                  ? 'Local Testing'
                  : 'Manual RTMP'}
              </Tag>
            </>
          ) : (
            <>
              <YoutubeOutlined style={{ color: '#ff0000', marginRight: '8px' }} />
              {previewStream?.name}
            </>
          )}
        </div>
      }
      open={open}
      onCancel={closePreview}
      width={960}
      destroyOnClose
      footer={[
        previewStream && !isLocalTestingStream(previewStream) && previewStream?.video_url && (
          <Button key="open" icon={<LinkOutlined />} onClick={() => window.open(previewStream?.video_url, '_blank')}>
            Open in YouTube
          </Button>
        ),
        <Button key="close" type="primary" onClick={closePreview}>
          Close
        </Button>
      ]}
    >
      {/* Playout-server HLS output. The manifest is NOT embedded: playout is
          served over HTTP and prod runs HTTPS, so an inline player trips the
          browser's mixed-content block. We link out to a new tab instead. */}
      {previewStream && (previewStream.rtmp_url || previewStream.output_target === 'amagi_hls') && !previewStream.video_url ? (
        <div>
          {previewStreamStatus && previewStreamStatus.ffmpeg?.isRunning ? (
            <div
              style={{
                padding: '16px',
                background: '#f6ffed',
                borderRadius: '4px',
                border: '1px solid #b7eb8f'
              }}
            >
              <Text strong style={{ color: '#389e0d' }}>Stream is live</Text>
              {previewStream.output_target === 'amagi_hls' && (
                <>
                  <div style={{ marginTop: '4px', marginBottom: '12px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      The HLS output cannot be embedded here — the playout server is served over HTTP
                      while this page is HTTPS (mixed content). Copy the manifest URL below and open it
                      in an external player (e.g. VLC).
                    </Text>
                  </div>
                  {getHlsUrl(previewStream) && (
                    <div style={{ marginBottom: '12px', fontSize: '11px', wordBreak: 'break-all' }}>
                      <Text type="secondary">HLS: </Text>
                      <Text
                        code
                        copyable={{ tooltips: ['Copy HLS URL', 'Copied'] }}
                        style={{ fontSize: '11px' }}
                      >
                        {getHlsUrl(previewStream)}
                      </Text>
                    </div>
                  )}
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      disabled={!getPreviewPageUrl(previewStream)}
                      onClick={() => window.open(getPreviewPageUrl(previewStream), '_blank', 'noopener,noreferrer')}
                    >
                      Open Preview Player
                    </Button>
                    <Button
                      icon={<CalendarOutlined />}
                      disabled={!getEpgUrl(previewStream)}
                      onClick={() => window.open(getEpgUrl(previewStream), '_blank', 'noopener,noreferrer')}
                    >
                      Open EPG
                    </Button>
                  </Space>
                </>
              )}
            </div>
          ) : (
            <div
              style={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                background: '#000',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#fff'
                }}
              >
                <LoadingOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div style={{ fontSize: '14px' }}>
                  {previewStreamStatus?.downloadQueue?.normalizing ? (
                    <div>
                      <div>Transcoding video…</div>
                      <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                        {previewStreamStatus.downloadQueue.normalizing.progress !== undefined
                          ? `Normalizing — ${previewStreamStatus.downloadQueue.normalizing.progress}%`
                          : 'Normalizing…'}
                      </div>
                    </div>
                  ) : previewStreamStatus?.downloadQueue?.isActive &&
                  (previewStreamStatus.downloadQueue.stateCounts?.downloading ?? 0) > 0 ? (
                    <div>
                      <div>Downloading files...</div>
                      <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                        Player will enable when stream starts
                      </div>
                    </div>
                  ) : isStarting || previewStreamStatus?.state === 'starting' ? (
                    <div>
                      <div>Starting…</div>
                      <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                        Preparing the first file — the player will enable when the stream is live
                      </div>
                    </div>
                  ) : (
                    <div>Waiting for stream to start...</div>
                  )}
                </div>
              </div>
            </div>
          )}
          {previewStreamStatus && !previewStreamStatus.ffmpeg?.isRunning ? (
            (() => {
              const queue = previewStreamStatus.downloadQueue;
              // "Preparing" covers the whole pre-live window — including the gap
              // right after Start (isStarting) and the server's 'starting' state,
              // before the download queue even exists — so we never flash the
              // misleading "Stream Not Running" while a start is in flight.
              const preparing =
                isStarting ||
                previewStreamStatus.state === 'starting' ||
                !!queue?.isActive ||
                (queue?.stateCounts?.downloading ?? 0) > 0 ||
                !!queue?.normalizing;
              const total = queue?.totalFiles ?? 0;
              const processed = queue
                ? queue.stateCounts.completed + queue.stateCounts.playing + queue.stateCounts.ready
                : 0;
              const overallPct = total > 0 ? Math.round((processed / total) * 100) : 0;
              const downloadingFile = queue?.allFiles?.find((f) => f.state === 'downloading');
              const normalizing = queue?.normalizing;
              return (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#fff7e6',
                    borderRadius: '4px',
                    border: '1px solid #ffd591'
                  }}
                >
                  <Text strong style={{ color: '#fa8c16' }}>
                    {preparing ? 'Preparing Stream' : 'Stream Not Running'}
                  </Text>
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    <Text type="secondary">
                      {preparing
                        ? 'Downloading and transcoding the first files. The player will start automatically once the stream is live.'
                        : 'The stream is currently not active. Start the playout to begin streaming.'}
                    </Text>
                  </div>

                  {preparing && queue && total > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      {/* Overall preparation progress (files downloaded + transcoded) */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Files ready</Text>
                        <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                          {processed}/{total} ({overallPct}%)
                        </Text>
                      </div>
                      <Progress
                        percent={overallPct}
                        status="active"
                        strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                      />

                      {/* Current download */}
                      {downloadingFile && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px', maxWidth: '70%' }}>
                              Downloading: {downloadingFile.videoTitle}
                            </Text>
                            <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                              {downloadingFile.downloadProgress ?? 0}%
                            </Text>
                          </div>
                          <Progress
                            percent={downloadingFile.downloadProgress ?? 0}
                            size="small"
                            status="active"
                            strokeColor="#13c2c2"
                          />
                        </div>
                      )}

                      {/* Current transcode (normalize) */}
                      {normalizing && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '12px', maxWidth: '70%' }}>
                              Transcoding: {normalizing.videoTitle}
                            </Text>
                            <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                              {normalizing.progress !== undefined ? `${normalizing.progress}%` : 'in progress…'}
                            </Text>
                          </div>
                          <Progress
                            percent={normalizing.progress ?? 0}
                            size="small"
                            status="active"
                            showInfo={normalizing.progress !== undefined}
                            strokeColor={{ '0%': '#722ed1', '100%': '#eb2f96' }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()
          ) : null}
        </div>
      ) : previewStream?.video_url ? (
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
            src={getYouTubeEmbedUrl(previewStream.video_url)}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      ) : null}

      {/* Server Status Section */}
      {previewStream?.playout_server_id && previewStreamStatus && (
        <div style={{ marginTop: '16px' }}>
          <Divider orientation="left">
            Playout Server Status
            <Tooltip title="Auto-refreshes every 3 seconds">
              <ReloadOutlined spin style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }} />
            </Tooltip>
          </Divider>
          <Card size="small">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Server Status</Text>
                  <div>
                    {previewStreamStatus.success ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Online
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="error">
                        Offline
                      </Tag>
                    )}
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">FFmpeg Status</Text>
                  <div>
                    {previewStreamStatus.ffmpeg?.isRunning ? (
                      <Tag color="processing" icon={<PlayCircleOutlined />}>
                        Running (PID: {previewStreamStatus.ffmpeg.pid})
                      </Tag>
                    ) : (
                      <Tag color="default">Idle</Tag>
                    )}
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Stream State</Text>
                  <div>
                    <Tag
                      color={
                        previewStreamStatus.state === 'playing'
                          ? 'success'
                          : previewStreamStatus.state === 'starting'
                          ? 'processing'
                          : previewStreamStatus.state === 'stopping'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {previewStreamStatus.state?.toUpperCase() || 'IDLE'}
                    </Tag>
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Uptime</Text>
                  <div>
                    <Text>
                      {previewStreamStatus.ffmpeg?.isRunning && previewStreamStatus.ffmpeg?.uptime
                        ? (() => {
                            const seconds = Math.floor(previewStreamStatus.ffmpeg.uptime / 1000);
                            const minutes = Math.floor(seconds / 60);
                            const hours = Math.floor(minutes / 60);
                            return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
                          })()
                        : '-'}
                    </Text>
                  </div>
                </Space>
              </Col>
              {/* Output Destinations: per-target RTMP push health (e.g. YouTube + Twitch) */}
              {previewStreamStatus.outputs?.rtmp?.destinations &&
                previewStreamStatus.outputs.rtmp.destinations.length > 0 && (
                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: '16px', marginBottom: '12px' }}>
                      <Text strong style={{ fontSize: '13px' }}>
                        Output Destinations ({previewStreamStatus.outputs.rtmp.destinations.length})
                      </Text>
                    </Divider>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {previewStreamStatus.outputs.rtmp.destinations.map((dest) => (
                        <div
                          key={dest.destination.url}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            flexWrap: 'wrap'
                          }}
                        >
                          <Space size="small">
                            <Text strong style={{ fontSize: '12px', textTransform: 'capitalize' }}>
                              {dest.destination.label || dest.destination.type}
                            </Text>
                            {dest.running ? (
                              <Tag icon={<CheckCircleOutlined />} color="success">
                                Live
                              </Tag>
                            ) : (
                              <Tag icon={<CloseCircleOutlined />} color="error">
                                Down
                              </Tag>
                            )}
                            {dest.restartCount > 0 && (
                              <Tag color="warning">
                                {dest.restartCount} restart{dest.restartCount > 1 ? 's' : ''}
                              </Tag>
                            )}
                          </Space>
                          {dest.running && dest.metrics && (
                            <Space size="small">
                              <Tag color={dest.metrics.fps >= 29 ? 'success' : 'warning'}>
                                {dest.metrics.fps} fps
                              </Tag>
                              <Tag color="blue">{dest.metrics.bitrate}</Tag>
                              <Tag color={dest.metrics.speed >= 0.95 ? 'success' : 'error'}>
                                {dest.metrics.speed}x
                              </Tag>
                            </Space>
                          )}
                        </div>
                      ))}
                      {previewStreamStatus.outputs.rtmp.destinations
                        .filter((d) => d.lastError)
                        .map((d) => (
                          <Text
                            key={`err-${d.destination.url}`}
                            type="danger"
                            style={{ fontSize: '11px' }}
                          >
                            {(d.destination.label || d.destination.type)}: {d.lastError}
                          </Text>
                        ))}
                    </Space>
                  </Col>
                )}
              {/* Stream Quality Metrics Section */}
              {previewStreamStatus.ffmpeg?.isRunning && previewStreamStatus.ffmpeg?.streamQuality && (
                <>
                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: '16px', marginBottom: '12px' }}>
                      <Text strong style={{ fontSize: '13px' }}>Stream Quality Metrics</Text>
                    </Divider>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>FPS</Text>
                      <div>
                        <Tag color={previewStreamStatus.ffmpeg.streamQuality.fps >= 29 ? 'success' : 'warning'}>
                          {previewStreamStatus.ffmpeg.streamQuality.fps.toFixed(1)}
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Bitrate</Text>
                      <div>
                        <Tag color="blue">{previewStreamStatus.ffmpeg.streamQuality.bitrate}</Tag>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Speed</Text>
                      <div>
                        <Tag color={previewStreamStatus.ffmpeg.streamQuality.speed >= 0.95 ? 'success' : 'error'}>
                          {previewStreamStatus.ffmpeg.streamQuality.speed.toFixed(2)}x
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Stream Time</Text>
                      <div>
                        <Text code style={{ fontSize: '11px' }}>{previewStreamStatus.ffmpeg.streamQuality.time}</Text>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Frame</Text>
                      <div>
                        <Text>{previewStreamStatus.ffmpeg.streamQuality.frame.toLocaleString()}</Text>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Dropped Frames</Text>
                      <div>
                        <Tag color={previewStreamStatus.ffmpeg.streamQuality.drop === 0 ? 'success' : 'warning'}>
                          {previewStreamStatus.ffmpeg.streamQuality.drop}
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Duplicate Frames</Text>
                      <div>
                        <Tag color={previewStreamStatus.ffmpeg.streamQuality.dup === 0 ? 'success' : 'warning'}>
                          {previewStreamStatus.ffmpeg.streamQuality.dup}
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                  <Col span={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Last Updated</Text>
                      <div>
                        <Text style={{ fontSize: '11px' }}>
                          {new Date(previewStreamStatus.ffmpeg.streamQuality.lastUpdated).toLocaleTimeString()}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                </>
              )}
              {previewStreamStatus.currentStream && (
                <>
                  <Col span={24}>
                    <div style={{ padding: '8px', background: '#f0f5ff', borderRadius: '4px', marginTop: '8px' }}>
                      <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>Current Stream Info</Text>
                      <div style={{ marginTop: '6px', fontSize: '11px' }}>
                        <div>
                          <Text type="secondary">Stream ID:</Text> <Text code>{previewStreamStatus.currentStream.id}</Text>
                        </div>
                        <div>
                          <Text type="secondary">Start Time:</Text>{' '}
                          <Text>{new Date(previewStreamStatus.currentStream.startTime).toLocaleString()}</Text>
                        </div>
                        <div>
                          <Text type="secondary">RTMP URL:</Text>{' '}
                          <Text code style={{ fontSize: '10px' }}>{previewStreamStatus.rtmpUrl}</Text>
                        </div>
                      </div>
                    </div>
                  </Col>
                </>
              )}
            </Row>
            {(previewStreamStatus.downloadQueue?.isActive ||
              (previewStreamStatus.downloadQueue?.allFiles?.length ?? 0) > 0 ||
              (previewStreamStatus.downloadQueue?.stateCounts?.downloading ?? 0) > 0) &&
              previewStreamStatus.downloadQueue && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}
                  >
                    <Text strong style={{ fontSize: '14px' }}>
                      Download Queue Status
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Total: {previewStreamStatus.downloadQueue.totalFiles} files
                    </Text>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Overall Progress
                      </Text>
                      <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                        {(() => {
                          const queue = previewStreamStatus.downloadQueue;
                          const total = queue.totalFiles;
                          const completed = queue.stateCounts.completed + queue.stateCounts.playing;
                          const ready = queue.stateCounts.ready;
                          const processed = completed + ready;
                          const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
                          return `${processed}/${total} (${percent}%)`;
                        })()}
                      </Text>
                    </div>
                    <Progress
                      percent={(() => {
                        const queue = previewStreamStatus.downloadQueue;
                        const total = queue.totalFiles;
                        const completed = queue.stateCounts.completed + queue.stateCounts.playing;
                        const ready = queue.stateCounts.ready;
                        const processed = completed + ready;
                        return total > 0 ? Math.round((processed / total) * 100) : 0;
                      })()}
                      status="active"
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068'
                      }}
                    />
                  </div>

                  {previewStreamStatus.downloadQueue.normalizing && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', maxWidth: '70%' }}>
                          Transcoding: {previewStreamStatus.downloadQueue.normalizing.videoTitle}
                        </Text>
                        <Text style={{ fontSize: '12px', fontWeight: 500 }}>
                          {previewStreamStatus.downloadQueue.normalizing.progress !== undefined
                            ? `${previewStreamStatus.downloadQueue.normalizing.progress}%`
                            : 'in progress…'}
                        </Text>
                      </div>
                      <Progress
                        percent={previewStreamStatus.downloadQueue.normalizing.progress ?? 0}
                        status="active"
                        showInfo={previewStreamStatus.downloadQueue.normalizing.progress !== undefined}
                        strokeColor={{ '0%': '#722ed1', '100%': '#eb2f96' }}
                      />
                    </div>
                  )}

                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <Tooltip title="Files waiting to be downloaded">
                        <div
                          style={{
                            background: '#f0f5ff',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #d6e4ff',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Pending</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#1890ff' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.pending}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="Files currently being downloaded from S3">
                        <div
                          style={{
                            background: '#e6fffb',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #87e8de',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Downloading</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#13c2c2' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.downloading}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="Files downloaded and currently transcoding to the broadcast format">
                        <div
                          style={{
                            background: '#f9f0ff',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #d3adf7',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Transcoding</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#722ed1' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.normalizing ?? 0}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="Files downloaded and ready to stream">
                        <div
                          style={{
                            background: '#f6ffed',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #b7eb8f',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Ready</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#52c41a' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.ready}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="File currently being streamed">
                        <div
                          style={{
                            background: '#fff7e6',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ffd591',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Playing</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#fa8c16' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.playing}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    <Col span={8}>
                      <Tooltip title="Files that finished streaming (may be deleted to save space)">
                        <div
                          style={{
                            background: '#fafafa',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9',
                            cursor: 'help'
                          }}
                        >
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Finished</div>
                          <div style={{ fontSize: '18px', fontWeight: 600, color: '#8c8c8c' }}>
                            {previewStreamStatus.downloadQueue.stateCounts.completed}
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                    {previewStreamStatus.downloadQueue.stateCounts.failed > 0 && (
                      <Col span={8}>
                        <Tooltip title="Files that failed to download or stream">
                          <div
                            style={{
                              background: '#fff1f0',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ffccc7',
                              cursor: 'help'
                            }}
                          >
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Failed</div>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: '#ff4d4f' }}>
                              {previewStreamStatus.downloadQueue.stateCounts.failed}
                            </div>
                          </div>
                        </Tooltip>
                      </Col>
                    )}
                  </Row>

                  <div
                    style={{
                      marginTop: '12px',
                      padding: '8px',
                      background: '#f0f5ff',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-around',
                      fontSize: '11px'
                    }}
                  >
                    <div>
                      <Text type="secondary">Downloaded: </Text>
                      <Text strong style={{ color: '#52c41a' }}>
                        {previewStreamStatus.downloadQueue.stateCounts.ready +
                          previewStreamStatus.downloadQueue.stateCounts.playing +
                          previewStreamStatus.downloadQueue.stateCounts.completed}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary">Streamed: </Text>
                      <Text strong style={{ color: '#fa8c16' }}>
                        {previewStreamStatus.downloadQueue.stateCounts.playing +
                          previewStreamStatus.downloadQueue.stateCounts.completed}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary">Remaining: </Text>
                      <Text strong style={{ color: '#1890ff' }}>
                        {previewStreamStatus.downloadQueue.stateCounts.pending +
                          previewStreamStatus.downloadQueue.stateCounts.downloading}
                      </Text>
                    </div>
                  </div>

                  {previewStreamStatus.downloadQueue.currentlyPlaying && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '10px',
                        background: '#fafafa',
                        borderRadius: '4px',
                        border: '1px solid #e8e8e8'
                      }}
                    >
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Currently Playing:</div>
                      <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                        {previewStreamStatus.downloadQueue.currentlyPlaying.videoTitle}
                      </div>
                      {((previewStreamStatus.downloadQueue.currentlyPlaying as any).fileName || 
                        (previewStreamStatus.downloadQueue.currentlyPlaying as any).file_name ||
                        (previewStreamStatus.downloadQueue.currentlyPlaying.localPath && 
                         previewStreamStatus.downloadQueue.currentlyPlaying.localPath.split('/').pop())) && (
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px', wordBreak: 'break-word' }}>
                          {(previewStreamStatus.downloadQueue.currentlyPlaying as any).fileName || 
                           (previewStreamStatus.downloadQueue.currentlyPlaying as any).file_name ||
                           (previewStreamStatus.downloadQueue.currentlyPlaying.localPath && 
                            previewStreamStatus.downloadQueue.currentlyPlaying.localPath.split('/').pop())}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        File {previewStreamStatus.downloadQueue.currentlyPlaying.index + 1} of{' '}
                        {previewStreamStatus.downloadQueue.totalFiles}
                      </div>
                    </div>
                  )}

                  {previewStreamStatus.downloadQueue?.allFiles &&
                    previewStreamStatus.downloadQueue.allFiles.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                          All Files ({previewStreamStatus.downloadQueue.allFiles.length}):
                        </Text>
                        <FileList
                          files={previewStreamStatus.downloadQueue.allFiles}
                          containerRef={fileListRef}
                          playingIndex={playingFileIndex}
                        />
                      </div>
                    )}

                  {(!previewStreamStatus.downloadQueue.allFiles ||
                    previewStreamStatus.downloadQueue.allFiles.length === 0) &&
                    previewStreamStatus.downloadQueue.upcomingFiles?.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                          Next {previewStreamStatus.downloadQueue.upcomingFiles.length} files:
                        </Text>
                        <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                          {previewStreamStatus.downloadQueue.upcomingFiles.slice(0, 5).map((file: any, idx: number) => (
                            <div
                              key={idx}
                              style={{
                                padding: '6px 8px',
                                marginBottom: '4px',
                                background: '#fafafa',
                                borderRadius: '4px',
                                fontSize: '11px'
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom:
                                    file.state === 'downloading' && file.downloadProgress !== undefined ? '4px' : '0'
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {idx + 1}. {file.videoTitle}
                                  </div>
                                  {((file as any).fileName || (file as any).file_name || (file.localPath && file.localPath.split('/').pop())) && (
                                    <div style={{ fontSize: '10px', color: '#999', marginTop: '2px', wordBreak: 'break-word' }}>
                                      {(file as any).fileName || (file as any).file_name || (file.localPath && file.localPath.split('/').pop())}
                                    </div>
                                  )}
                                </div>
                                <Tag
                                  color={
                                    file.state === 'ready'
                                      ? 'success'
                                      : file.state === 'downloading'
                                      ? 'processing'
                                      : file.state === 'normalizing'
                                      ? 'purple'
                                      : 'default'
                                  }
                                  style={{ margin: 0, fontSize: '10px', marginLeft: '8px' }}
                                >
                                  {file.state === 'normalizing' ? 'transcoding' : file.state}
                                </Tag>
                              </div>
                              {file.state === 'downloading' && file.downloadProgress !== undefined && (
                                <div style={{ marginTop: '4px' }}>
                                  <Progress
                                    percent={Math.round(file.downloadProgress)}
                                    size="small"
                                    status="active"
                                    strokeColor={{
                                      '0%': '#108ee9',
                                      '100%': '#87d068'
                                    }}
                                    style={{ margin: 0 }}
                                  />
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: '10px', display: 'block', marginTop: '2px', textAlign: 'right' }}
                                  >
                                    {Math.round(file.downloadProgress)}%
                                  </Text>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

            {previewStreamStatus.downloadQueue?.allFiles &&
              previewStreamStatus.downloadQueue.allFiles.length > 0 &&
              !(previewStreamStatus.downloadQueue?.isActive || (previewStreamStatus.downloadQueue?.stateCounts?.downloading ?? 0) > 0) && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                  <Text strong style={{ fontSize: '14px', marginBottom: '12px', display: 'block' }}>Files Status</Text>
                  <FileList
                    files={previewStreamStatus.downloadQueue.allFiles}
                    containerRef={fileListRef}
                    playingIndex={playingFileIndex}
                  />
                </div>
              )}
          </Card>
        </div>
      )}

      {isOAuthStream && (
        <div style={{ marginTop: '16px' }}>
          <Divider orientation="left">
            YouTube Broadcasts
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={broadcastsLoading}
              onClick={() => handleRefreshBroadcasts()}
              style={{ padding: 0, marginLeft: '8px' }}
            >
              Refresh
            </Button>
          </Divider>
          {broadcastsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
              <Spin />
            </div>
          ) : broadcasts.length === 0 ? (
            <Text type="secondary">No broadcasts found for this stream.</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {broadcasts.map((broadcast) => (
                <Card key={broadcast.id} size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Space direction="vertical" size={0}>
                        <Text strong>Broadcast</Text>
                        <Text code style={{ fontSize: '11px' }}>{broadcast.youtube_broadcast_id}</Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Created: {broadcast.created_at ? new Date(broadcast.created_at).toLocaleString() : '-'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Scheduled: {broadcast.scheduled_start_time ? new Date(broadcast.scheduled_start_time).toLocaleString() : '-'}
                        </Text>
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Space direction="vertical" size={4}>
                        <div>
                          <Tag color={broadcast.broadcast_status === 'live' ? 'success' : 'blue'}>
                            {broadcast.broadcast_status || 'unknown'}
                          </Tag>
                          {broadcast.broadcast_privacy_status && (
                            <Tag color="default">{broadcast.broadcast_privacy_status}</Tag>
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Last polled: {broadcast.last_polled_at ? new Date(broadcast.last_polled_at).toLocaleString() : '-'}
                        </Text>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          )}
        </div>
      )}

      {isOAuthStream && (
        <div style={{ marginTop: '16px' }}>
          <Divider orientation="left">
            YouTube Stream Health
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={streamHealthLoading}
              disabled={!canRefreshStreamHealth}
              onClick={handleRefreshStreamHealth}
              style={{ padding: 0, marginLeft: '8px' }}
            >
              Refresh
            </Button>
          </Divider>
          <Card size="small">
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Stream ID</Text>
                  <Text code style={{ fontSize: '11px' }}>
                    {streamHealth?.youtube_oauth_stream_id || '-'}
                  </Text>
                  {!canRefreshStreamHealth && (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      No OAuth stream has been created yet.
                    </Text>
                  )}
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Status</Text>
                  <div>
                    <Tag color={streamHealth?.stream_status === 'active' ? 'success' : 'default'}>
                      {streamHealth?.stream_status || 'unknown'}
                    </Tag>
                    <Tag color={streamHealth?.stream_health_status === 'good' ? 'success' : 'warning'}>
                      {streamHealth?.stream_health_status || 'unknown'}
                    </Tag>
                  </div>
                </Space>
              </Col>
              <Col span={24}>
                <Space size="small">
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Last updated: {streamHealth?.updated_at ? new Date(streamHealth.updated_at).toLocaleString() : '-'}
                  </Text>
                  {streamHealth?.stream_health_details && (
                    <Popover
                      title="Health Details"
                      content={
                        <pre style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto', fontSize: '11px', margin: 0 }}>
                          {JSON.stringify(streamHealth.stream_health_details, null, 2)}
                        </pre>
                      }
                    >
                      <Button type="link" size="small" style={{ padding: 0 }}>
                        View details
                      </Button>
                    </Popover>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </div>
      )}

      {previewStreamStatus?.cleanupLogs && previewStreamStatus.cleanupLogs.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Divider orientation="left">Cleanup Activity Log</Divider>
          <Card size="small">
            <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '11px' }}>
              {previewStreamStatus.cleanupLogs.slice().reverse().map((log: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    padding: '8px',
                    marginBottom: '6px',
                    borderRadius: '4px',
                    background:
                      log.action === 'cleaned'
                        ? '#f6ffed'
                        : log.action === 'skipped_threshold'
                        ? '#e6f7ff'
                        : log.action === 'skipped_window'
                        ? '#fff7e6'
                        : '#fff1f0',
                    border: `1px solid ${
                      log.action === 'cleaned'
                        ? '#b7eb8f'
                        : log.action === 'skipped_threshold'
                        ? '#91d5ff'
                        : log.action === 'skipped_window'
                        ? '#ffd591'
                        : '#ffccc7'
                    }`
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}
                  >
                    <Tag
                      color={
                        log.action === 'cleaned'
                          ? 'success'
                          : log.action === 'skipped_threshold'
                          ? 'blue'
                          : log.action === 'skipped_window'
                          ? 'orange'
                          : 'error'
                      }
                      style={{ margin: 0, fontSize: '10px' }}
                    >
                      {log.action === 'cleaned'
                        ? '🗑️ CLEANED'
                        : log.action === 'skipped_threshold'
                        ? '⏸️ SKIPPED (SIZE)'
                        : log.action === 'skipped_window'
                        ? '⏸️ SKIPPED (WINDOW)'
                        : '❌ FAILED'}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </Text>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '10px', marginBottom: '2px' }}>
                    File #{log.fileIndex}: {log.fileName}
                  </div>
                  {log.reason && (
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                      {log.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {previewStream?.description && (
        <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
          <Text strong>Description:</Text>
          <div style={{ marginTop: '8px' }}>
            <Text>{previewStream.description}</Text>
          </div>
        </div>
      )}

      {previewStream?.rtmp_url && (
        <div style={{ marginTop: '16px' }}>
          <Card size="small" title="Stream Connection Details">
            <div style={{ marginBottom: '8px' }}>
              <Text strong>RTMP URL:</Text>
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  marginTop: '4px',
                  padding: '8px',
                  background: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                {previewStream.rtmp_url}
              </div>
            </div>
            {previewStream.stream_key && (
              <div>
                <Text strong>Stream Key:</Text>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    marginTop: '4px',
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '4px'
                  }}
                >
                  {previewStream.stream_key}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default PreviewModal;
